import { stringifyGraph } from '@/clients/http'
import { ProductModel } from '@/http/models/Product/Product'
import { toast } from 'sonner'

import { useCallback, useEffect, useState } from 'react'

import { ProductVariationModel } from '@/types/model/ProductVariation'

export interface StockLevelItem {
  _id: string
  productName: string
  sku: string
  quantity: number
  minStockQuantity: number
  status: 'in-stock' | 'low-stock' | 'out-of-stock'
}

export interface StockStats {
  totalVariations: number
  inStock: number
  lowStock: number
  outOfStock: number
}

const LOW_STOCK_DEFAULT = 5

function deriveStatus(qty: number, min: number): StockLevelItem['status'] {
  if (qty <= 0) return 'out-of-stock'
  if (qty <= min) return 'low-stock'
  return 'in-stock'
}

export const useFetchStockLevels = () => {
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState<StockLevelItem[]>([])

  const fetch = useCallback(async () => {
    setLoading(true)

    const res = await ProductModel.getList({
      query: {
        page: 1,
        length: 500,
        graph_enc: stringifyGraph({
          '*': true,
          variations: {
            '*': true,
            'stocks(quantity!=0)': {
              _id: true,
              quantity: true,
            },
          },
        }) as any,
      },
    })

    if (!res.success) {
      toast.error('Failed to load stock levels')
      setLoading(false)
      return
    }

    const products: any[] = (res.data as any)?.result ?? []
    const flat: StockLevelItem[] = []

    for (const product of products) {
      for (const variation of (product.variations ?? []) as (ProductVariationModel.ProductVariation & { product?: { name: string } })[]) {
        const stocks: { quantity: number }[] = (variation as any).stocks ?? []
        const qty = stocks.reduce((sum, s) => sum + s.quantity, 0)
        const min = (variation as any).min_stock_quantity ?? LOW_STOCK_DEFAULT
        flat.push({
          _id: variation._id,
          productName: product.name ?? '—',
          sku: variation.sku ?? '—',
          quantity: qty,
          minStockQuantity: min,
          status: deriveStatus(qty, min),
        })
      }
    }

    setItems(flat)
    setLoading(false)
  }, [])

  useEffect(() => {
    fetch()
  }, [fetch])

  const stats: StockStats = {
    totalVariations: items.length,
    inStock: items.filter((i) => i.status === 'in-stock').length,
    lowStock: items.filter((i) => i.status === 'low-stock').length,
    outOfStock: items.filter((i) => i.status === 'out-of-stock').length,
  }

  return { loading, items, stats, refresh: fetch }
}
